import React from 'react';
import { Link, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { PublicNavbar } from '@/components/Layout/PublicNavbar';
import { FormField } from '@/components/forms/FormField';
import { PasswordField } from '@/components/forms/PasswordField';
import { loginSchema, type LoginFormData } from '@/lib/validations/auth';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { CaptchaField, type CaptchaFieldHandle } from '@/components/Auth/CaptchaField';
import { verifyCaptcha } from '@/lib/captcha';

// After this many failed login attempts in the current session, require an
// hCaptcha challenge before the next submit. Matches the decision in
// docs/release-plan-v1.md §4.
const LOGIN_CAPTCHA_THRESHOLD = 3;

const Login: React.FC = () => {
  const { toast } = useToast();
  const { signIn, signInWithGoogle, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
      rememberMe: false,
    },
  });

  const [failedAttempts, setFailedAttempts] = React.useState(0);
  const [captchaToken, setCaptchaToken] = React.useState('');
  const captchaRef = React.useRef<CaptchaFieldHandle | null>(null);
  const captchaRequired = failedAttempts >= LOGIN_CAPTCHA_THRESHOLD;

  // Redirect if already authenticated. Default to `/app` so PostLoginRedirect
  // can route organizers vs guests to the right landing page.
  if (user) {
    const from = location.state?.from?.pathname || '/app';
    return <Navigate to={from} replace />;
  }

  const onSubmit = async (data: LoginFormData) => {
    try {
      // After the threshold of failed attempts, require an hCaptcha token
      // server-verified before we even try to authenticate.
      if (captchaRequired) {
        if (!captchaToken) {
          setError('root', { message: 'Por favor verifica que no eres un robot.' });
          return;
        }

        const captchaResult = await verifyCaptcha(captchaToken, 'login');
        if (!captchaResult.success) {
          captchaRef.current?.reset();
          setCaptchaToken('');
          toast({
            variant: 'destructive',
            title: 'Captcha inválido',
            description: 'No pudimos verificar el captcha. Inténtalo de nuevo.',
          });
          return;
        }
      }

      const { error } = await signIn(data.email, data.password);

      if (error) {
        if (error.message.includes('Invalid login credentials')) {
          setError('root', {
            message: 'Credenciales incorrectas. Verifica tu email y contraseña.',
          });
        } else {
          setError('root', {
            message: error.message,
          });
        }

        setFailedAttempts((n) => n + 1);
        // Reset captcha after every failed attempt so the user must re-solve
        // before the next try once the threshold is crossed.
        captchaRef.current?.reset();
        setCaptchaToken('');

        toast({
          variant: "destructive",
          title: "Error de inicio de sesión",
          description: error.message,
        });
        return;
      }

      toast({
        title: "Inicio de sesión exitoso",
        description: "Bienvenido de vuelta",
      });

      // Navigate to intended destination
      const from = location.state?.from?.pathname || '/app/dashboard';
      navigate(from, { replace: true });
    } catch (error) {
      setError('root', {
        message: 'Error inesperado. Por favor intenta de nuevo.',
      });

      toast({
        variant: "destructive",
        title: "Error de inicio de sesión",
        description: "Error inesperado. Por favor intenta de nuevo.",
      });
    }
  };

  const handleGoogleLogin = async () => {
    try {
      const { error } = await signInWithGoogle();
      
      if (error) {
        toast({
          variant: "destructive",
          title: "Error con Google",
          description: error.message,
        });
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error con Google",
        description: "No se pudo iniciar sesión con Google. Por favor intenta de nuevo.",
      });
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      <PublicNavbar />
      
      <div className="pt-32 pb-12 px-4 sm:px-6 lg:px-8 animate-fade-in">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-8 animate-fade-in-scale">
            <h2 className="text-h1 font-bold text-brand-primary">
              Inicia sesión en tu cuenta
            </h2>
            <p className="mt-2 text-body-md text-neutral-600">
              ¿No tienes cuenta?{' '}
              <Link 
                to="/signup" 
                className="font-medium text-brand-primary hover:text-brand-primary-hover transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2 rounded-sm"
              >
                Regístrate gratis
              </Link>
            </p>
          </div>

          <div className="bg-white py-8 px-4 shadow-brand-lg sm:rounded-lg sm:px-10 border border-neutral-200 animate-fade-in-scale hover:shadow-xl transition-all duration-300">
            <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
              {errors.root && (
                <div role="alert" className="bg-semantic-error-light border border-semantic-error text-semantic-error px-4 py-3 rounded-md animate-fade-in">
                  {errors.root.message}
                </div>
              )}

              <div className="animate-fade-in">
                <FormField
                  label="Correo electrónico"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  autoFocus
                  placeholder="tu@email.com"
                  error={errors.email}
                  {...register('email')}
                />
              </div>

              <div className="animate-fade-in">
                <PasswordField
                  label="Contraseña"
                  name="password"
                  autoComplete="current-password"
                  required
                  placeholder="Tu contraseña"
                  error={errors.password}
                  {...register('password')}
                />
              </div>

              <div className="flex items-center justify-between animate-fade-in">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    type="checkbox"
                    className="h-4 w-4 text-brand-primary focus:ring-brand-primary border-neutral-300 rounded transition-colors duration-200"
                    {...register('rememberMe')}
                  />
                  <Label htmlFor="remember-me" className="ml-2 block text-body-sm text-neutral-700">
                    Recuérdame
                  </Label>
                </div>

                <div className="text-body-sm">
                  <Link 
                    to="/forgot-password" 
                    className="font-medium text-brand-primary hover:text-brand-primary-hover transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2 rounded-sm"
                  >
                    ¿Olvidaste tu contraseña?
                  </Link>
                </div>
              </div>

              {captchaRequired && (
                <div className="animate-fade-in">
                  <p className="text-body-sm text-neutral-600 mb-2">
                    Por seguridad, verifica que no eres un robot.
                  </p>
                  <CaptchaField
                    ref={captchaRef}
                    onVerify={(token) => setCaptchaToken(token)}
                  />
                </div>
              )}

              <div className="animate-fade-in">
                <Button
                  type="submit"
                  disabled={isSubmitting || (captchaRequired && !captchaToken)}
                  className="w-full bg-brand-primary hover:bg-brand-primary-hover text-white shadow-brand hover:shadow-brand-lg transition-all duration-200 hover:-translate-y-0.5"
                >
                  {isSubmitting ? 'Iniciando sesión...' : 'Iniciar sesión'}
                </Button>
              </div>

              <div className="mt-6 animate-fade-in">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-neutral-300" />
                  </div>
                  <div className="relative flex justify-center text-body-sm">
                    <span className="px-2 bg-white text-neutral-500">o</span>
                  </div>
                </div>

                <div className="mt-6">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleGoogleLogin}
                    className="w-full border-2 border-neutral-300 hover:border-brand-primary hover:bg-neutral-50 transition-all duration-200 hover:-translate-y-0.5"
                  >
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                      <path
                        fill="currentColor"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="currentColor"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="currentColor"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="currentColor"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                    Continuar con Google
                  </Button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
